import { BusinessObjectBase } from './base';
import { saleSchema } from '@schemas/business-objects/sale';
import type { SalePhoto } from './sale-photo';
import type { CalendarDate, Id } from '../base-types';
import type { MeasurementType } from './measurement-type';
import type { SystemTaskExecution } from './system-task-execution';

export interface SaleData {
  readonly id: Id;
  readonly name: string;
  readonly startDate?: CalendarDate;
  readonly endDate?: CalendarDate;
  readonly salePhotos?: SalePhoto[];
}

export interface Sale extends Readonly<SaleData> {}
export class Sale extends BusinessObjectBase {
  static readonly entityName = 'Sale' as const;
  static readonly plural = 'sales';
  static schema = saleSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Sale[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Sale(data));
  }

  static async findOne(filter?: any): Promise<Sale | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Sale(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Sale> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Sale(data);
  }

  static async create(data: Omit<SaleData, 'id'>): Promise<Sale> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Sale(result);
  }

  static async createMany(data: Omit<SaleData, 'id'>[]): Promise<Sale[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Sale(item));
  }

  static async updateById(id: Id, updates: Partial<SaleData>): Promise<Sale> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Sale(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async readBanners(): Promise<number> {
    const url = `${BusinessObjectBase.baseUrl}/${Sale.plural}/${(this as any).id}/read-banners`;
    return BusinessObjectBase.post<number>(url, {});
  }
  async exportMeasurementsCsv(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Sale.plural}/${(this as any).id}/export-measurements-csv`;
    return BusinessObjectBase.get<any>(url);
  }
}
